import { apiConfig } from "./api-config";
import {
  REFRESH_COOKIE_ABSENT,
  REFRESH_COOKIE_STATE_HEADER,
  hasRefreshCookie,
} from "./auth-bootstrap";

// Auth-aware fetch wrapper: on a 401, calls POST /api/auth/refresh (rotating
// the DB-backed refresh token — see apps/api-go/internal/usecase/auth's
// Refresh()) and retries the original request exactly once. This is the
// client-side half of the refresh-token mechanism; the access JWT stays a
// short-lived stateless token (see JWT_ACCESS_TOKEN_TTL_MINUTES) while this
// wrapper keeps the user's session alive transparently.
//
// Two call sites, two cookie-delivery mechanisms:
// - Browser (no `request` passed): the browser's own cookie jar sends/
//   receives cookies automatically via `credentials: "include"`, so no
//   manual header plumbing is needed here.
// - SSR (a `request` is passed, from a Cloudflare Workers `fetch` handler):
//   there is no browser cookie jar — the incoming request's Cookie header is
//   forwarded explicitly, and any rotated Set-Cookie from the refresh
//   response must be captured and later reattached to the outer Response via
//   flushAuthCookies(), or the browser never learns about the rotation.

const capturedSetCookiesByRequest = new WeakMap<Request, string[]>();
const refreshByRequest = new WeakMap<Request, Promise<RefreshOutcome>>();
let pendingBrowserRefresh: Promise<RefreshOutcome> | null = null;

type RefreshOutcome = { ok: boolean; setCookies: string[] };

// 提示した refresh token の値をキーに、isolate 全体で refresh を1本に畳む。
//
// 以前は WeakMap<Request, ...> で「SSR の1リクエスト単位」の重複排除だけを
// していた。React Router はページ遷移・prefetch・.data 取得をそれぞれ別の
// Request として走らせるので、互いを知らないまま同じ token で同時に
// /api/auth/refresh を叩いていた。本番ログでは1バースト3〜6本、5秒で12本に
// なっていた。
//
// サーバー側の10秒の猶予はバースト内の重複を吸収するが、猶予で通った側は
// Rotated: false になり refresh Cookie を返さない (古い値へ巻き戻さないための
// 設計)。つまり回転後の新しい token は「最初に当たった1本のレスポンス」に
// しか乗らず、それが prefetch などでブラウザに反映されないと、ブラウザは
// 古い token を持ち続ける。次のバーストで「10秒より前に回転済み」の token を
// 出すことになり、盗用とみなされて family ごと revoke される
// (api-go の refresh: reuse detected, family revoked)。全タブが落ちる。
//
// ここでは「同一isolate内で今まさに進行中の呼び出し」だけを畳む。完了結果を
// raw tokenをキーに保持し続けると、再送者へ後継tokenを配るキャッシュになり
// セキュリティ境界が曖昧になる。isolateをまたぐ競合と完了直後の再送はAPI側の
// 決定論的successor + 10秒graceで同じSet-Cookieを返して吸収する。
const MAX_PENDING_REFRESHES = 64;
const pendingRefreshByToken = new Map<string, Promise<RefreshOutcome>>();

// React Router's single-fetch `.data` path clones the incoming Request before
// it calls loaders. The loader therefore captures cookies under a different
// object identity from the FetchEvent Request that workers/app.ts later
// flushes. Keep the same bounded hand-off keyed by the predecessor token so
// the outer response can recover it. The lifetime deliberately matches the
// API's 10-second duplicate-refresh grace; this does not extend the period in
// which an old credential can obtain its deterministic successor.
const CAPTURED_REFRESH_COOKIE_TTL_MS = 10_000;
type CapturedRefreshCookies = { cookies: string[]; expiresAt: number };
const capturedSetCookiesByRefreshToken = new Map<
  string,
  CapturedRefreshCookies
>();

function rememberRefreshCookies(token: string, cookies: string[]): void {
  const now = Date.now();
  for (const [key, value] of capturedSetCookiesByRefreshToken) {
    if (value.expiresAt <= now) capturedSetCookiesByRefreshToken.delete(key);
  }
  if (
    !capturedSetCookiesByRefreshToken.has(token) &&
    capturedSetCookiesByRefreshToken.size >= MAX_PENDING_REFRESHES
  ) {
    return;
  }
  capturedSetCookiesByRefreshToken.set(token, {
    cookies: [...cookies],
    expiresAt: now + CAPTURED_REFRESH_COOKIE_TTL_MS,
  });
}

// Cloudflare Workers は1リクエストの処理中に開いたまま(レスポンスボディ未消費)の
// fetch が一定数を超えると、デッドロック防止のため古いものを強制キャンセルする
// ("stalled HTTP response was canceled to prevent deadlock")。React Router は
// 同一ルートツリーのlayout/pageのloaderを並列実行するため、1回のSSRリクエストで
// GraphQL呼び出しが3〜4本同時に飛ぶことがあり、これがキャンセル(≒ログイン状態
// 消失)を引き起こしていた。原因(Cookie失効・欠落を問わず)に関わらず、1リクエ
// ストあたりの同時fetch数そのものに上限をかけて安全側に倒す。
const MAX_CONCURRENT_FETCHES_PER_REQUEST = 3;

type ConcurrencyState = { active: number; queue: Array<() => void> };
const concurrencyStateByRequest = new WeakMap<Request, ConcurrencyState>();

async function acquireFetchSlot(request: Request): Promise<() => void> {
  let state = concurrencyStateByRequest.get(request);
  if (!state) {
    state = { active: 0, queue: [] };
    concurrencyStateByRequest.set(request, state);
  }
  if (state.active >= MAX_CONCURRENT_FETCHES_PER_REQUEST) {
    await new Promise<void>((resolve) => state!.queue.push(resolve));
  }
  state.active++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    state!.active--;
    const next = state!.queue.shift();
    if (next) next();
  };
}

function getSetCookies(response: Response): string[] {
  const headers = response.headers as Headers & {
    getSetCookie?: () => string[];
  };
  if (typeof headers.getSetCookie === "function") {
    return headers.getSetCookie();
  }
  const single = response.headers.get("set-cookie");
  return single ? [single] : [];
}

function captureResponseCookies(request: Request, response: Response): void {
  const setCookies = getSetCookies(response);
  if (setCookies.length === 0) return;

  // A single route action can make more than one auth request (signup then
  // OTP, for example). Preserve header order instead of replacing an earlier
  // Set-Cookie so the browser sees the complete upstream response.
  const captured = capturedSetCookiesByRequest.get(request) ?? [];
  capturedSetCookiesByRequest.set(request, [...captured, ...setCookies]);
}

function mergeCookieHeaders(existing: string, fresh: string): string {
  const jar = new Map<string, string>();
  for (const part of [existing, fresh].join(";").split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf("=");
    if (eq === -1) continue;
    jar.set(trimmed.slice(0, eq), trimmed.slice(eq + 1));
  }
  return Array.from(jar.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join("; ");
}

function getCookieValue(cookieHeader: string, name: string): string | null {
  let value: string | null = null;
  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === name) {
      // 同名Cookieが旧Path=/api/authと現行Path=/の両方にある場合、長い
      // Pathの値が先に送られる。最後の値を採り、回転済みの旧Cookieを
      // refreshの畳み込みキーにしない。
      const rawValue = trimmed.slice(eq + 1);
      try {
        value = decodeURIComponent(rawValue);
      } catch {
        // A malformed percent escape must not turn an otherwise recoverable
        // auth request into an SSR 500. The API will reject the raw value and
        // return the normal cookie-clearing 401 path.
        value = rawValue;
      }
    }
  }
  return value;
}

function decodeJwtExpSeconds(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

const EXPIRY_SKEW_MS = 10_000;

// アクセスJWT(session_token)が失効済み/失効間近か、あるいはそもそも欠落して
// いるか(session_token だけ削除されたが refresh_token は残っている場合を含む)
// を、実際にリクエストを送る前に Cookie ヘッダーだけを見て判定する。
//
// session_token が丸ごと無い場合を「stale ではない」として素通りさせると、
// 一部のGraphQLクエリ(例: myTeams)は未認証を HTTP 401 ではなく200+空データ
// で返す設計になっており(該当リゾルバがログアウト状態と区別せず既定値を返す
// ため)、401ベースの事後refreshが一切発火しないまま「チーム未所属」等の
// 誤判定でリダイレクトされてしまう。refresh_token が残っているなら、
// session_token の有無に関わらず先に refresh を試みる。
//
// SSR の 1 リクエスト内では複数のloaderが同時に fetch を発行するため、401 を
// 受けてから refresh する方式だと全員が同時に 401→refresh→retry の波を起こし、
// Cloudflare Workers の同時接続上限に達して古いレスポンスが強制キャンセルされる
// (=ログイン状態が失われる)。事前判定で refresh を先出しし、この波自体を起こ
// さないようにする。
function shouldProactivelyRefresh(cookieHeader: string): boolean {
  const hasRefreshToken =
    getCookieValue(cookieHeader, "__Secure-better-auth.refresh_token") !==
      null ||
    getCookieValue(cookieHeader, "better-auth.refresh_token") !== null;
  if (!hasRefreshToken) return false;

  const token =
    getCookieValue(cookieHeader, "__Secure-better-auth.session_token") ??
    getCookieValue(cookieHeader, "better-auth.session_token");
  if (!token) return true;

  const exp = decodeJwtExpSeconds(token);
  if (exp === null) return true;
  return exp * 1000 <= Date.now() + EXPIRY_SKEW_MS;
}

function applyFreshCookies(
  init: RequestInit | undefined,
  existingCookieHeader: string,
  freshSetCookies: string[],
): RequestInit | undefined {
  if (freshSetCookies.length === 0) return init;
  const freshCookieHeader = freshSetCookies
    .map((c) => c.split(";")[0])
    .join("; ");
  const headers = new Headers(init?.headers);
  headers.set(
    "Cookie",
    mergeCookieHeaders(existingCookieHeader, freshCookieHeader),
  );
  return { ...init, headers };
}

/** 実際に /api/auth/refresh を1回叩く。畳み込みは呼び出し側でやる */
async function callRefresh(cookieHeader: string): Promise<RefreshOutcome> {
  const refreshUrl = `${apiConfig.getApiUrl()}/api/auth/refresh`;
  const headers: Record<string, string> = {};
  if (cookieHeader) headers["Cookie"] = cookieHeader;

  let response: Response;
  try {
    response = await fetch(refreshUrl, {
      method: "POST",
      headers,
      credentials: "include",
    });
  } catch {
    return { ok: false, setCookies: [] };
  }

  // 401でもapi-goは失効Cookieを削除するSet-Cookieを返す。これを捨てると
  // ブラウザが無効なrefresh tokenを送り続け、全ページでrefresh 401を
  // 繰り返すため、成否にかかわらず必ず外側Responseへ引き継ぐ。
  return { ok: response.ok, setCookies: getSetCookies(response) };
}

/** Cookie ヘッダーから refresh token の値だけ取り出す。畳み込みのキーになる */
function getRefreshTokenValue(cookieHeader: string): string | null {
  return (
    getCookieValue(cookieHeader, "__Secure-better-auth.refresh_token") ??
    getCookieValue(cookieHeader, "better-auth.refresh_token")
  );
}

function callSharedRefresh(
  token: string,
  cookieHeader: string,
): Promise<RefreshOutcome> {
  const existing = pendingRefreshByToken.get(token);
  if (existing) return existing;

  // Never evict an in-flight promise. Under a flood of distinct tokens,
  // calls above the cap bypass only this optimization; already-tracked
  // tokens remain correctly coalesced until their request settles.
  if (pendingRefreshByToken.size >= MAX_PENDING_REFRESHES) {
    return callRefresh(cookieHeader);
  }

  const promise = callRefresh(cookieHeader);
  pendingRefreshByToken.set(token, promise);
  const cleanup = () => {
    if (pendingRefreshByToken.get(token) === promise) {
      pendingRefreshByToken.delete(token);
    }
  };
  void promise.then(cleanup, cleanup);
  return promise;
}

async function refreshAuthCookies(request?: Request): Promise<boolean> {
  const cookieHeader = request?.headers.get("cookie") ?? "";
  const token = request ? getRefreshTokenValue(cookieHeader) : null;

  let outcome: RefreshOutcome;

  if (request) {
    // refresh Cookie を持たない要求で refresh を撃っても、api-go は
    // Set-Cookie 無しの 401 を返すだけ。未ログインの訪問者の SSR が
    // 401 のたびにこれを撃ち、refresh の 96% が 401 になっていた
    // 同名 Cookie の末尾が空でも、ほかに値があれば api-go は更新できる
    if (!hasRefreshCookie(cookieHeader)) return false;

    // A single SSR Request can reach this once proactively and once again
    // after a 401. Reuse its first result until flushAuthCookies so an
    // invalid token does not cause two refresh calls in one request.
    const existing = refreshByRequest.get(request);
    const promise =
      existing ??
      (token
        ? callSharedRefresh(token, cookieHeader)
        : callRefresh(cookieHeader));
    if (!existing) refreshByRequest.set(request, promise);
    outcome = await promise;
  } else {
    // ブラウザ側。Cookie は自前で持っているので、isolate 内で1本に畳むだけ
    const promise = pendingBrowserRefresh ?? callRefresh("");
    pendingBrowserRefresh = promise;
    try {
      outcome = await promise;
    } finally {
      if (pendingBrowserRefresh === promise) pendingBrowserRefresh = null;
    }
  }

  if (request && outcome.setCookies.length > 0) {
    const captured = capturedSetCookiesByRequest.get(request) ?? [];
    capturedSetCookiesByRequest.set(request, [
      ...captured,
      ...outcome.setCookies,
    ]);
    if (token) rememberRefreshCookies(token, outcome.setCookies);
  }

  return outcome.ok;
}

function isAuthPathInput(input: RequestInfo | URL): boolean {
  try {
    const raw =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    return new URL(raw, "https://placeholder.invalid").pathname.startsWith(
      "/api/auth/",
    );
  } catch {
    return false;
  }
}

// createAuthAwareFetch returns a fetch-compatible function that transparently
// refreshes and retries once on a 401. Pass the incoming SSR `request` when
// called from a Cloudflare Workers loader/action context; omit it in the
// browser.
export function createAuthAwareFetch(
  request?: Request,
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return async (input, init) => {
    let effectiveInit = init;

    // SSRでは1リクエストの中で複数のloaderが並列にfetchを発行する。401を
    // 受けてから refresh する方式だけだと、アクセスJWT失効直後は全員が同時に
    // 401→refresh→retry の波を起こし、Cloudflare Workers の同時接続上限に
    // 達して古いレスポンスが強制キャンセルされる(≒ログイン状態が失われる)。
    // 送信前にJWTの exp を見て、失効していれば先に(重複排除済みで)refresh
    // してから本番のfetchを送ることで、この波自体を起こさないようにする。
    if (request) {
      const cookieHeader = request.headers.get("cookie");
      if (cookieHeader && shouldProactivelyRefresh(cookieHeader)) {
        await refreshAuthCookies(request);

        const fresh = capturedSetCookiesByRequest.get(request);
        if (fresh) {
          effectiveInit = applyFreshCookies(init, cookieHeader, fresh);
        }
      }
    }

    const releaseSlot = request ? await acquireFetchSlot(request) : null;
    let response: Response;
    try {
      response = await fetch(input, effectiveInit);
    } finally {
      releaseSlot?.();
    }
    if (request) captureResponseCookies(request, response);
    if (response.status !== 401) {
      return response;
    }

    // 事前チェックをすり抜けた場合(exp判定の誤差、または初回401)向けの
    // フォールバック。従来どおり401→refresh→1回だけretry。
    // 同時に来た呼び出しの畳み込みは refreshAuthCookies が token 単位で見る
    // ブラウザからは HttpOnly の refresh Cookie が見えない。Worker の中継が
    // 「refresh Cookie は来ていない」と印を付けた 401 なら、refresh しても
    // 同じ 401 が返るだけなので撃たない
    // 印は Worker が /api/auth/ の下の 401 にだけ付ける。ほかの経路の応答に
    // 同じ見出しがあっても(上流が付けた等)信じない
    if (
      !request &&
      isAuthPathInput(input) &&
      response.headers.get(REFRESH_COOKIE_STATE_HEADER) ===
        REFRESH_COOKIE_ABSENT
    ) {
      return response;
    }
    const refreshed = await refreshAuthCookies(request);
    if (!refreshed) {
      return response;
    }

    let retryInit = effectiveInit;
    if (request) {
      const fresh = capturedSetCookiesByRequest.get(request);
      const existingCookie =
        new Headers(effectiveInit?.headers).get("Cookie") ?? "";
      if (fresh) {
        retryInit = applyFreshCookies(effectiveInit, existingCookie, fresh);
      }
    }

    const releaseRetrySlot = request ? await acquireFetchSlot(request) : null;
    try {
      const retryResponse = await fetch(input, retryInit);
      if (request) captureResponseCookies(request, retryResponse);
      return retryResponse;
    } finally {
      releaseRetrySlot?.();
    }
  };
}

// flushAuthCookies reattaches any Set-Cookie headers captured during a
// same-request refresh onto the outer Response, so the browser actually
// receives the rotated cookies. Call this once, right before returning the
// Response from a Cloudflare Workers `fetch` handler (see
// apps/client/workers/app.ts and apps/shegate-web/workers/app.ts).
export function flushAuthCookies(
  request: Request,
  response: Response,
): Response {
  let setCookies = capturedSetCookiesByRequest.get(request);
  if (!setCookies || setCookies.length === 0) {
    const cookieHeader = request.headers.get("cookie") ?? "";
    const token = getRefreshTokenValue(cookieHeader);
    const captured = token
      ? capturedSetCookiesByRefreshToken.get(token)
      : undefined;
    if (captured && captured.expiresAt > Date.now()) {
      setCookies = captured.cookies;
    } else if (token && captured) {
      capturedSetCookiesByRefreshToken.delete(token);
    }
  }
  if (!setCookies || setCookies.length === 0) {
    return response;
  }

  capturedSetCookiesByRequest.delete(request);
  refreshByRequest.delete(request);

  const existing = new Set(getSetCookies(response));
  for (const cookie of setCookies) {
    if (!existing.has(cookie)) {
      response.headers.append("Set-Cookie", cookie);
      existing.add(cookie);
    }
  }

  return response;
}
